import AppGrid from "@/features/order-sheet-sets/app-grid";
import { useRecoilState } from "recoil";
import { buyerLineSheetSetRowsState } from "@/features/order-sheet-sets/recoils/buyer-line-sheet-set-rows-state";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useCallback, useEffect, useMemo } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useBuyerLineSheetSetRows from "@/features/order-sheet-sets/hooks/use-buyer-line-sheet-set-rows";
import UploadOrderSheetsModal from "@/features/order-sheet-sets/upload-order-sheets-modal";
import { Flex, Text, Tooltip } from "@chakra-ui/react";
import { findParentGridRow } from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import OrderOrderSheetSetModal from "@/features/order-sheet-sets/order-order-sheet-set-modal";
import OrderSheetRowTooltipLabel from "@/features/order-sheet-sets/order-sheet-row-tooltip-label";
import {
  findYWRInfiniteSegment,
  postProcessYWRInfiniteData,
} from "@/features/ui/helpers/ywr-helpers";
import {
  BuyerAttachmentRow,
  BuyerLineSheetRow,
  BuyerLineSheetSetRow,
} from "@/features/order-sheet-sets/line-sheet-set-row.type";
import useReadLineSheetSetInfinite from "@/features/line-sheet-sets/hooks/use-read-line-sheet-set-infinite";
import { readLineSheetSetKey } from "@/features/line-sheet-sets/hooks/use-read-line-sheet-set";
import {
  ListDenseLineSheetSetsResponse,
  readDenseLineSheetSetProactively,
} from "@/features/line-sheet-sets/hooks/use-list-dense-line-sheet-sets";
import useAppGridMutate from "@/features/ui/hooks/use-app-grid-mutate";
import { toLower, toTitle, toUpper } from "@/utils/case";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useLineSheetSetGrid from "@/features/order-sheet-sets/hooks/use-line-sheet-set-grid";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import AppOrderSheetStatusLabel from "@/features/ui/app-order-sheet-status-label";
import { BuyerOrderSheetRow } from "@/features/order-status/order-sheet-set-row.type";
import usePersistedLineSheetSetListQuery from "@/features/line-sheet-sets/hooks/use-persisted-line-sheet-set-list-query";
import { highlightPartialText } from "@/utils/highlightSearch";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";
import useCreateOrderSheetSetInfinite from "@/features/line-sheet-sets/hooks/use-create-order-sheet-set-infinite";
import OrderStatusBadge from "@/features/order-status/OrderStatusBadge";
import StatusBadge from "@/shared/components/StatusBadge";

interface Props {
  denseLineSheet?: ListDenseLineSheetSetsResponse;
}

export default function BuyerLineSheetSetGrid({ denseLineSheet }: Props) {
  const identity = useIdentity();
  const { error: showError } = useAppToasts();
  const [rows, setRows] = useRecoilState(buyerLineSheetSetRowsState);
  const { hover, toggleCollapseIfRoot, toggleCheck } = useAppGridMutate(
    buyerLineSheetSetRowsState
  );
  const { mutateRows } = useBuyerLineSheetSetRows();
  const { discloseTooltip } = useAppGridMutate(buyerLineSheetSetRowsState);
  const {
    t,
    tLocalDateString,
    tLocalDateTimeString,
    tLocalDateTimeString2,
    tCurrencyString,
  } = useI18nHelper();

  const { navigateToLineSheetSetPage, navigateToBuyerOrderSheetSetPage } =
    useRouterHelper();

  const {
    disclosureUploadOrderSheetModal,
    disclosureOrderOrderSheetSetModal,
    actions,
    fields,
    extraContents,
  } = useLineSheetSetGrid({
    rows,
    discloseTooltip,
    mutateRows,
  });

  const { value: query } = usePersistedLineSheetSetListQuery();

  // --- API: Read Line Sheet Set ---
  const {
    fire: fireReadLineSheetSet,
    data: readLineSheetSetData,
    clear: clearReadLineSheetSet,
  } = useReadLineSheetSetInfinite();

  const { fire: fireCreateOrderSheetSet } = useCreateOrderSheetSetInfinite();

  useEffect(() => {
    postProcessYWRInfiniteData(
      readLineSheetSetData,
      clearReadLineSheetSet,
      showError
    );
  }, [readLineSheetSetData, clearReadLineSheetSet, showError]);

  const findReadLineSheetSetSegment = useCallback(
    (row: BuyerLineSheetSetRow) => {
      if (identity?.company) {
        return findYWRInfiniteSegment(
          readLineSheetSetData,
          readLineSheetSetKey,
          {
            by: identity.company.type,
            companyId: identity.company.id,
            lineSheetSetId: row.id,
          }
        );
      }
    },
    [identity, readLineSheetSetData]
  );

  const allBrandList = useGetCompanies(["BRAND"]);

  const selectedBrands = useMemo(() => {
    return (
      query["madeByCompany.id__in"]
        ?.map(
          (company) =>
            allBrandList?.find((brand) => brand.id === +company)?.name
        )
        .filter((brand): brand is string => !!brand) ?? []
    );
  }, [allBrandList, query]);

  // --- END: API: Read Line Sheet Set ---
  return (
    <>
      <AppGrid
        metas={[
          {
            rowType: "LineSheetSet",
            gap: "4px",
            columns: [
              { name: toTitle(t("season")), width: "48px" },
              { name: toTitle(t("brand")), width: "144px" },
              { name: toTitle(t("supplier")), width: "80px" },
              { name: toTitle(t("sheet_title")), width: "minmax(224px, 1fr)" },
              {
                name: toTitle(t("ordered_amount.abbr")),
                width: "132px",
                textAlign: "end",
                marginRight: "24px",
              },
              { name: toTitle(t("published_on")), width: "88px" },
              { name: toTitle(t("order_valid_until")), width: "128px" },
              { name: toTitle(t("status")), width: "128px" },
            ],
            fields: [
              {
                name: "season",
                value: (r) => {
                  return (r as BuyerLineSheetSetRow).season;
                },
              },
              {
                name: "Brand",
                value: (r) => {
                  const brands =
                    (denseLineSheet as any)?.lineSheetSets
                      .find(
                        (item: any) =>
                          item.id === (r as BuyerLineSheetSetRow).id
                      )
                      ?.brands.map((brand: any) => brand.name) || [];
                  const term = query.query ?? "";
                  const keywords = [term, ...selectedBrands]
                    .filter((kw): kw is string => !!kw)
                    .join("|");
                  const regex = new RegExp(keywords, "i");

                  const matching = brands.filter((brand: string) =>
                    regex.test(brand)
                  );
                  const others = brands.filter(
                    (brand: string) => !regex.test(brand)
                  );

                  // 4) 매칭된 순서 유지(혹은 하나만) + 나머지 알파벳 순 정렬
                  const orderedBrands = [...matching, ...others];

                  // 5) 문자열로 합치기
                  const brandList = orderedBrands.join(", ");
                  const minW =
                    brandList && brandList.length > 3000
                      ? 1600
                      : brandList && brandList.length > 1000
                      ? 800
                      : undefined;

                  return (
                    <Tooltip
                      label={highlightPartialText(
                        brandList,
                        [query.query, ...selectedBrands],
                        true
                      )}
                      hasArrow={true}
                      placement={"top"}
                      openDelay={500}
                      maxW={minW}
                    >
                      <Text noOfLines={2}>
                        {highlightPartialText(brandList, [
                          query.query,
                          ...selectedBrands,
                        ])}
                      </Text>
                    </Tooltip>
                  );
                },
              },
              {
                name: "IssuedBy",
                value: (r) => {
                  return (r as BuyerLineSheetSetRow).issuedByBoutiqueName;
                },
              },
              {
                name: "Title",
                isBold: true,
                value: (r) => {
                  const title = highlightPartialText(
                    (r as BuyerLineSheetSetRow).name,
                    [query.query]
                  );
                  const lineSheetSetId = (r as BuyerLineSheetSetRow).id;
                  const searchedSkus = highlightPartialText(
                    (r as BuyerLineSheetSetRow).searchedSkus?.at(0),
                    [query.query]
                  );
                  const canOrderByUnit = (r as BuyerLineSheetSetRow)
                    .canOrderByUnit;
                  const isFeatured = (r as BuyerLineSheetSetRow).isFeatured;
                  return (
                    <Flex flexDirection={"column"}>
                      <Flex gap={"6px"} alignItems={"center"}>
                        {/* 어드민이 고른 우선 노출 세트 — 목록 맨 위에 순번대로 온다. */}
                        {isFeatured && (
                          <StatusBadge
                            status={toTitle(t("featured_line_sheet_set"))}
                            colorVariant={"yellow"}
                            size={"small"}
                          />
                        )}
                        <Text textStyle={"body-sm-bold"}>
                          #{lineSheetSetId} {title}
                        </Text>
                        {/* 패스픽에 올라간 스탁오더 세트 — 이 세트만 단품오더가 된다. */}
                        {canOrderByUnit && (
                          <StatusBadge
                            status={toTitle(t("by_unit_order_available"))}
                            colorVariant={"blueFilled"}
                            size={"small"}
                          />
                        )}
                      </Flex>
                      {searchedSkus && (
                        <Text textStyle={"caption-regular"}>
                          ...{searchedSkus}
                        </Text>
                      )}
                    </Flex>
                  );
                },
              },
              {
                name: "Ordered Amount",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const orderedAmount = (r as BuyerLineSheetSetRow)
                    .orderedAmount;
                  const orderedAmountWithoutCondition = (
                    r as BuyerLineSheetSetRow
                  ).orderedAmountWithoutCondition;
                  const orderedConditionType = (r as BuyerLineSheetSetRow)
                    .orderedConditionType;
                  let conditionStr = "";
                  if (orderedConditionType === "DISCOUNT") {
                    conditionStr = "R- ";
                  } else if (orderedConditionType === "MARKUP") {
                    conditionStr = "C+ ";
                  } else if (orderedConditionType === "MIX") {
                    conditionStr = "R/C ";
                  }
                  if (orderedAmount == 0) {
                    return "";
                  }
                  return (
                    <Flex
                      flexDirection={"column"}
                      fontSize={"12px"}
                      alignItems={"flex-end"}
                      marginRight={"24px"}
                    >
                      <Text fontWeight={"700"}>
                        {tCurrencyString(orderedAmountWithoutCondition, {
                          type: "SYMBOL",
                          code: (r as BuyerLineSheetSetRow).currency,
                        })}
                      </Text>
                      <Text>
                        ({conditionStr}
                        {tCurrencyString(orderedAmount, {
                          type: "SYMBOL",
                          code: (r as BuyerLineSheetSetRow).currency,
                        })}
                        )
                      </Text>
                    </Flex>
                  );
                },
              },
              {
                name: "publishedOn",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const value = (r as BuyerLineSheetSetRow).publishedAt;
                  return tLocalDateString(value);
                },
              },
              {
                name: "validUntil",
                value: (r) => {
                  const value = (r as BuyerLineSheetSetRow).validUntil;
                  return tLocalDateTimeString2(value);
                },
              },
              {
                name: "status",
                value: (_row, context) => {
                  const row = _row as BuyerLineSheetSetRow;
                  if (row.orderSheetSetStatus) {
                    return (
                      // <AppOrderSheetStatusLabel
                      //   value={row.orderSheetSetStatus}
                      // />
                      <OrderStatusBadge status={row.orderSheetSetStatus} />
                    );
                  }
                  return "";
                },
              },
              // fields.withOrderSheetStatus(true),
            ],
            actions: [
              // actions.orderOrderSheetSet,
              // actions.cancelOrderSheetSet,
              actions.withDownloadUnoFile<BuyerLineSheetSetRow>((row) => {
                return row.compressedImportedFile;
              }),
              actions.withDownloadUnoForm<BuyerLineSheetSetRow>((row) => {
                return (
                  row.orderSheetSetCompressedUnoFormFile ||
                  row.compressedUnoFormFile
                );
              }),
            ],
            indicator: (row) => {
              return (
                <GridRowIndicator
                  type={"VIEW"}
                  isHovered={row.isHovered}
                  isCollapsed={row.isCollapsed}
                />
              );
            },
            onClick: (row) => {
              const lineSheetSetRow = row as BuyerLineSheetSetRow;
              localStorage.setItem("selectedLineSheetSetRow", row._rowId);

              if (lineSheetSetRow.status !== "PARSING_COMPLETED") {
                navigateToLineSheetSetPage(
                  lineSheetSetRow.id,
                  query.query,
                  query["madeByCompany.id__in"],
                  query.season__in
                );
              } else if (lineSheetSetRow.orderSheetSetId > 0) {
                navigateToBuyerOrderSheetSetPage(
                  lineSheetSetRow.orderSheetSetId,
                  lineSheetSetRow.id,
                  query.query,
                  query["madeByCompany.id__in"],
                  query.season__in
                );
              } else {
                if (identity?.company) {
                  fireCreateOrderSheetSet(
                    {
                      by: identity.company.type,
                      companyId: identity.company.id,
                    },
                    {
                      lineSheetSet: {
                        id: lineSheetSetRow.id,
                      },
                    }
                  ).then((data) => {
                    if (data?.id) {
                      navigateToBuyerOrderSheetSetPage(
                        data.id,
                        lineSheetSetRow.id,
                        query.query,
                        query["madeByCompany.id__in"],
                        query.season__in
                      );
                    }
                  });
                }
              }
            },
          },
          {
            rowType: "LineSheet",
            columns: [
              { name: "type", width: "232px" },
              { name: "name", width: "minmax(224px, 1fr)" },
              { name: "status", width: "144px" },
            ],
            fields: [
              fields.lineSheetType,
              {
                name: "name",
                chakraProps: {
                  fontWeight: 700,
                },
                value: (r) => {
                  return (r as BuyerLineSheetRow).name;
                },
              },
            ],
            actions: [
              actions.writeDraft,
              actions.writeFallbackDraft,
              actions.viewLineSheet,
              actions.withDownloadUnoFile<BuyerLineSheetRow>((row) => {
                return row.file;
              }),
            ],
            tooltip: (_row) => {
              const row = _row as BuyerLineSheetRow;
              if (row.isWritable) {
                return (
                  <OrderSheetRowTooltipLabel isFallback={row.isFallback} />
                );
              }
            },
          },
          {
            rowType: "Attachment",
            columns: [
              { name: "type", width: "232px" },
              { name: "name", width: "minmax(224px, 1fr)" },
              { name: "status", width: "144px" },
            ],
            fields: [
              {
                name: "type",
                value: (row) => {
                  const attachmentRow = row as BuyerAttachmentRow;
                  const i = attachmentRow.name.lastIndexOf(".");
                  let suffix = "";
                  if (i > -1) {
                    suffix =
                      "/" + attachmentRow.name.substring(i + 1).toUpperCase();
                  }
                  return `${toUpper(t("attachment"))}${suffix}`;
                },
              },
              {
                name: "name",
                value: (row) => {
                  const attachmentRow = row as BuyerAttachmentRow;
                  return attachmentRow.name;
                },
              },
            ],
            actions: [
              actions.withDownloadUnoFile<BuyerAttachmentRow>((row) => row),
            ],
          },
          {
            rowType: "OrderSheet",
            columns: [
              { name: "type", width: "152px" },
              { name: "name", width: "minmax(224px, 1fr)" },
              { name: "Ordered Amount", width: "144px" },
              { name: "boutiqueName ", width: "104px" },
              { name: "status", width: "144px" },
            ],
            fields: [
              { name: "type", value: (r) => toUpper(t("order_sheet")) },
              {
                name: "name",
                chakraProps: {
                  fontWeight: 700,
                },
                value: (row) => {
                  const orderSheetRow = row as BuyerOrderSheetRow;
                  const lineSheetRow = findParentGridRow(
                    orderSheetRow,
                    rows
                  ) as BuyerLineSheetRow;
                  return lineSheetRow.name;
                },
              },
              {
                name: "OrderedAmount",
                value: (r) => {
                  const orderSheetRow = r as BuyerOrderSheetRow;
                  return (
                    <Text textAlign={"end"} marginEnd={"24px"}>
                      {tCurrencyString(orderSheetRow.orderedAmount, {
                        type: "SYMBOL",
                        code: (r as BuyerLineSheetSetRow).currency,
                      })}
                    </Text>
                  );
                },
              },
              {
                name: "boutiqueName",
                value: (r) => {
                  const orderSheetRow = r as BuyerOrderSheetRow;
                  return orderSheetRow.toBeSubmitted?.name || "";
                },
              },
              {
                name: "status",
                value: (_row, context) => {
                  const row = _row as BuyerOrderSheetRow;
                  // return <AppOrderSheetStatusLabel value={row.status} />;
                  return <OrderStatusBadge status={row.status} />;
                },
              },
            ],
            actions: [
              actions.editOrViewOrderSheet,
              actions.replaceOrUploadUploadedFile,
              actions.deleteUploadedFile,
              actions.withDownloadUnoFile<BuyerOrderSheetRow>(
                (row) => row.uploadedFile || row.exportedFile
              ),
            ],
            extraContents: extraContents.orderSheetExtraContents,
          },
        ]}
        rows={rows}
        onHover={(path, row, isHovered) => {
          hover(row, isHovered);
        }}
        onClick={(path, _row) => {
          if (_row._rowType === "LineSheetSet") {
            const row = _row as BuyerLineSheetSetRow;

            if (!row.isRead && identity?.company) {
              readDenseLineSheetSetProactively(row.id);

              const isLoading =
                findReadLineSheetSetSegment(row)?.isLoading || false;
              if (!isLoading) {
                fireReadLineSheetSet(
                  {
                    by: identity.company.type,
                    companyId: identity.company.id,
                    lineSheetSetId: row.id,
                  },
                  {}
                );
              }
            }

            //toggleCollapseIfRoot(row);
          }
        }}
        onCheck={(path, row, isChecked) => {
          toggleCheck(row);
        }}
      ></AppGrid>
      {disclosureUploadOrderSheetModal.data && (
        <UploadOrderSheetsModal
          {...disclosureUploadOrderSheetModal.data}
          isOpen={disclosureUploadOrderSheetModal.isOpen}
          onClose={disclosureUploadOrderSheetModal.onClose}
        />
      )}
      {disclosureOrderOrderSheetSetModal.data && (
        <OrderOrderSheetSetModal
          {...disclosureOrderOrderSheetSetModal.data}
          isOpen={disclosureOrderOrderSheetSetModal.isOpen}
          onClose={disclosureOrderOrderSheetSetModal.onClose}
        />
      )}
    </>
  );
}
